/**
 * CodeSkin "Shop for me" agent engine.
 *
 * Deterministic and data-only: every attribute, price and stock value comes
 * from the catalog that Liquid prints from Shopify (agent.* metafields, then
 * category/concern collection membership, then the product title for SPF).
 * Nothing here invents product facts; unknown data is reported as unknown.
 *
 * Exposed as window.CodeSkinAgent in the browser and module.exports in Node
 * (for tests).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.CodeSkinAgent = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Vocabulary (must match the agent.* metafield choice lists in Shopify admin)
  // ---------------------------------------------------------------------------
  var VOCAB = {
    category: ['sunscreen', 'facewash', 'moisturizer', 'serum', 'spot-treatment', 'pre-cleanser', 'hair-care'],
    skin_types: ['oily', 'dry', 'combination', 'normal', 'sensitive'],
    concerns: ['acne', 'pigmentation', 'ageing', 'sensitivity', 'dehydration', 'oil-control', 'dullness', 'pores', 'sun-protection'],
    usage_time: ['am', 'pm', 'both'],
    finish: ['matte', 'natural', 'dewy']
  };

  var LABELS = {
    sunscreen: 'Sunscreen', facewash: 'Face wash', moisturizer: 'Moisturiser', serum: 'Serum',
    'spot-treatment': 'Targeted treatment', 'pre-cleanser': 'Pre-cleanser', 'hair-care': 'Hair care',
    oily: 'Oily', dry: 'Dry', combination: 'Combination', normal: 'Normal', sensitive: 'Sensitive',
    acne: 'Acne', pigmentation: 'Pigmentation', ageing: 'Ageing', sensitivity: 'Sensitivity',
    dehydration: 'Dehydration', 'oil-control': 'Oil control', dullness: 'Dullness', pores: 'Pores',
    'sun-protection': 'Sun protection', am: 'Morning (AM)', pm: 'Night (PM)', both: 'AM & PM',
    matte: 'Matte', natural: 'Natural', dewy: 'Dewy',
    gel: 'Gel', fluid: 'Fluid', lotion: 'Lotion', cream: 'Cream'
  };

  // Shopify collection handle -> attribute, used when agent.* metafields are empty.
  var CATEGORY_COLLECTIONS = {
    sunscreen: 'sunscreen', facewash: 'facewash', moisturizer: 'moisturizer', serum: 'serum',
    'targeted-treatments': 'spot-treatment', 'pre-cleanser': 'pre-cleanser', 'hair-care': 'hair-care'
  };
  var CONCERN_COLLECTIONS = {
    'acne-prone-skin': ['acne'], 'hyper-pigmentation': ['pigmentation'], patchy: ['pigmentation'],
    'ageing-skin': ['ageing'], 'sensitive-skin': ['sensitivity'], sensitive: ['sensitivity'],
    dehydration: ['dehydration'], 'daily-protection': ['sun-protection'], protection: ['sun-protection'],
    'oily-skin': ['oil-control'], 'oil-control': ['oil-control'], brightening: ['dullness']
  };
  var SKIN_TYPE_COLLECTIONS = { 'oily-skin': ['oily'], 'sensitive-skin': ['sensitive'] };

  // Routine order (matches the chatbot's category order).
  var ROUTINE_STEP = { 'pre-cleanser': 1, facewash: 2, serum: 3, 'spot-treatment': 3, moisturizer: 4, sunscreen: 5 };
  var STEP_NAMES = { 1: 'Pre-cleanse', 2: 'Cleanse', 3: 'Treat', 4: 'Moisturise', 5: 'Protect' };

  function label(v) { return LABELS[v] || v; }
  function uniq(arr) { return arr.filter(function (v, i) { return v != null && v !== '' && arr.indexOf(v) === i; }); }
  function inVocab(list, v) { return VOCAB[list].indexOf(v) !== -1; }
  function asList(v) {
    if (v == null || v === '') return [];
    if (Array.isArray(v)) return v;
    return String(v).split(',');
  }
  function norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function rupees(paise) { return Math.round(paise / 100); }

  // ---------------------------------------------------------------------------
  // Catalog normalisation
  // ---------------------------------------------------------------------------

  /** Turns one raw catalog entry (from Liquid) into a product with attributes + sources. */
  function normalizeProduct(raw) {
    var mf = raw.agent || {};
    var cols = (raw.collections || []).map(norm);
    var attrs = {};
    var source = {};

    function set(key, value, src) {
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) return;
      attrs[key] = value;
      source[key] = src;
    }

    // category
    var mfCat = norm(mf.category);
    if (inVocab('category', mfCat)) set('category', mfCat, 'metafield');
    else {
      for (var i = 0; i < cols.length; i++) {
        if (CATEGORY_COLLECTIONS[cols[i]]) { set('category', CATEGORY_COLLECTIONS[cols[i]], 'collection'); break; }
      }
    }

    // skin types
    var mfSkin = asList(mf.skin_types).map(norm).filter(function (v) { return inVocab('skin_types', v); });
    if (mfSkin.length) set('skin_types', uniq(mfSkin), 'metafield');
    else {
      var colSkin = [];
      cols.forEach(function (c) { (SKIN_TYPE_COLLECTIONS[c] || []).forEach(function (s) { colSkin.push(s); }); });
      set('skin_types', uniq(colSkin), 'collection');
    }

    // concerns
    var mfCon = asList(mf.concerns).map(norm).filter(function (v) { return inVocab('concerns', v); });
    if (mfCon.length) set('concerns', uniq(mfCon), 'metafield');
    else {
      var colCon = [];
      cols.forEach(function (c) { (CONCERN_COLLECTIONS[c] || []).forEach(function (s) { colCon.push(s); }); });
      if (attrs.category === 'sunscreen') colCon.push('sun-protection');
      set('concerns', uniq(colCon), attrs.category === 'sunscreen' && !colCon.length ? 'category' : 'collection');
    }

    var usage = norm(mf.usage_time);
    if (inVocab('usage_time', usage)) set('usage_time', usage, 'metafield');
    else if (attrs.category === 'sunscreen') set('usage_time', 'am', 'category');

    var finish = norm(mf.finish);
    if (inVocab('finish', finish)) set('finish', finish, 'metafield');
    if (mf.texture) set('texture', norm(mf.texture), 'metafield');

    if (typeof mf.white_cast === 'boolean') set('white_cast', mf.white_cast, 'metafield');

    var spf = parseInt(mf.spf, 10);
    if (spf > 0) set('spf', spf, 'metafield');
    else {
      var m = /spf\s*-?\s*(\d{2,3})/i.exec(raw.title || '');
      if (m) set('spf', parseInt(m[1], 10), 'title');
    }
    if (mf.pa) set('pa', String(mf.pa), 'metafield');

    if (typeof mf.pregnancy_safe === 'boolean') set('pregnancy_safe', mf.pregnancy_safe, 'metafield');
    if (typeof mf.fragrance_free === 'boolean') set('fragrance_free', mf.fragrance_free, 'metafield');

    var step = parseInt(mf.routine_step, 10);
    if (step >= 1 && step <= 5) set('routine_step', step, 'metafield');
    else if (attrs.category && ROUTINE_STEP[attrs.category]) set('routine_step', ROUTINE_STEP[attrs.category], 'category');

    if (mf.key_ingredients) set('key_ingredients', asList(mf.key_ingredients).map(function (s) { return String(s).trim(); }), 'metafield');
    if (mf.avoid_with) set('avoid_with', asList(mf.avoid_with).map(String), 'metafield');
    if (mf.compatible_with) set('compatible_with', asList(mf.compatible_with).map(String), 'metafield');

    var variants = (raw.variants || []).map(function (v) {
      return { id: v.id, title: v.title, price: Number(v.price) || 0, available: !!v.available, sku: v.sku || '' };
    });
    var available = variants.filter(function (v) { return v.available; });
    var cheapest = (available.length ? available : variants).slice().sort(function (a, b) { return a.price - b.price; })[0] || null;

    return {
      id: String(raw.id),
      handle: raw.handle,
      title: raw.title,
      url: raw.url,
      image: raw.image || '',
      variants: variants,
      variant: cheapest,
      price: cheapest ? cheapest.price : 0,
      available: available.length > 0,
      attrs: attrs,
      source: source
    };
  }

  function normalizeCatalog(rawList) {
    return (rawList || []).map(normalizeProduct);
  }

  // ---------------------------------------------------------------------------
  // Intent
  // ---------------------------------------------------------------------------

  function emptyIntent() {
    return {
      mode: 'products', category: null, skin_types: [], concerns: [], max_price: null,
      usage_time: null, white_cast: null, spf_min: null, pregnancy_safe: null,
      fragrance_free: null, finish: null, unclear: []
    };
  }

  /** Keeps only known vocabulary values, so nothing outside the catalog's terms gets through. */
  function sanitizeIntent(input) {
    var i = emptyIntent();
    input = input || {};
    i.mode = input.mode === 'routine' ? 'routine' : 'products';
    var cat = norm(input.category);
    i.category = inVocab('category', cat) ? cat : null;
    i.skin_types = uniq(asList(input.skin_types).map(norm).filter(function (v) { return inVocab('skin_types', v); }));
    i.concerns = uniq(asList(input.concerns).map(norm).filter(function (v) { return inVocab('concerns', v); }));
    var mp = Number(input.max_price);
    i.max_price = mp > 0 ? Math.round(mp) : null;
    var ut = norm(input.usage_time);
    i.usage_time = inVocab('usage_time', ut) ? ut : null;
    i.white_cast = input.white_cast === false ? false : null;
    var spf = parseInt(input.spf_min, 10);
    i.spf_min = spf > 0 ? spf : null;
    i.pregnancy_safe = input.pregnancy_safe === true ? true : null;
    i.fragrance_free = input.fragrance_free === true ? true : null;
    var fin = norm(input.finish);
    i.finish = inVocab('finish', fin) ? fin : null;
    i.unclear = asList(input.unclear).map(String).filter(Boolean).slice(0, 5);
    return i;
  }

  var CATEGORY_WORDS = [
    ['sunscreen', /\b(sun\s*screen|sunscreen|sun\s*block|sunblock|sun\s*cream|spf)\b/],
    ['facewash', /\b(face\s*wash|facewash|cleanser|face\s*cleanser)\b/],
    ['pre-cleanser', /\b(micellar|cleansing\s*oil|pre[\s-]*cleans\w*|make\s*-?up\s*remover)\b/],
    ['moisturizer', /\b(moisturi[sz]\w*|moisturer)\b/],
    ['serum', /\bserums?\b/],
    ['spot-treatment', /\b(spot\s*(treatment|corrector|zapper|gel)?|targeted\s*treatment)\b/],
    ['hair-care', /\b(hair|scalp|shampoo|conditioner|dandruff)\b/]
  ];
  var CONCERN_WORDS = [
    ['acne', /\b(acne|pimples?|breakouts?|zits?|blackheads?|whiteheads?)\b/],
    ['pigmentation', /\b(pigment\w*|dark\s*spots?|melasma|tan(ning)?|uneven(\s*(skin\s*)?tone)?|patch\w*)\b/],
    ['ageing', /\b(age?ing|anti[\s-]*age?ing|wrinkles?|fine\s*lines?|firm\w*|sagg\w*)\b/],
    ['sensitivity', /\b(sensitivity|redness|irritat\w*|reactive)\b/],
    ['dehydration', /\b(dehydrat\w*|hydrat\w*|dryness)\b/],
    ['oil-control', /\b(oil[\s-]*control|greasy|shine|shiny|sebum)\b/],
    ['dullness', /\b(dull\w*|bright\w*|glow\w*|radian\w*)\b/],
    ['pores', /\bpores?\b/],
    ['sun-protection', /\b(sun\s*protection|uv|sun\s*damage)\b/]
  ];
  var SKIN_WORDS = [
    ['oily', /\boily\b/], ['dry', /\bdry\s*skin\b|\bmy\s*skin\s*is\s*dry\b|\bdry\b(?!\s*weather)/],
    ['combination', /\b(combination|combo)\b/], ['normal', /\bnormal\s*skin\b/],
    ['sensitive', /\bsensitive\b/]
  ];

  /** Fallback parser used when the Claude intent service is not configured or fails. */
  function parseIntentLocal(text) {
    // "₹1,500" / "1,50,000": drop digit-grouping commas before anything else.
    var t = ' ' + norm(text).replace(/(\d),(?=\d)/g, '$1').replace(/[^\w₹.\s-]/g, ' ') + ' ';
    var raw = { skin_types: [], concerns: [] };

    for (var c = 0; c < CATEGORY_WORDS.length; c++) {
      if (CATEGORY_WORDS[c][1].test(t)) { raw.category = CATEGORY_WORDS[c][0]; break; }
    }
    SKIN_WORDS.forEach(function (p) { if (p[1].test(t)) raw.skin_types.push(p[0]); });
    CONCERN_WORDS.forEach(function (p) { if (p[1].test(t)) raw.concerns.push(p[0]); });

    var price = /(?:under|below|less\s*than|max(?:imum)?|budget(?:\s*of)?|up\s*to|upto|within|around|₹|rs\.?|inr)\s*(?:₹|rs\.?|inr)?\s*(\d+(?:\.\d+)?)\s*(k)?/.exec(t) ||
      /(\d+(?:\.\d+)?)\s*(k)?\s*(?:₹|rs\.?|rupees|inr)\b/.exec(t) ||
      /(\d+(?:\.\d+)?)\s*(k)?\s*(?:ke\s*andar|tak|se\s*kam|ke\s*niche)\b/.exec(t);
    if (price) raw.max_price = parseFloat(price[1]) * (price[2] ? 1000 : 1);

    if (/\b(morning|day\s*time|daytime|during\s*the\s*day|am)\b/.test(t)) raw.usage_time = 'am';
    else if (/\b(night|evening|bed\s*time|bedtime|pm)\b/.test(t)) raw.usage_time = 'pm';

    // "no white cast", "without a white cast", "I don't want white cast", "avoid white cast"
    if (/\b(no|without|zero|avoid|hate|don\s*t\s*want|dont\s*want|do\s*not\s*want|not\s*want|doesn\s*t\s*leave|non)\b[\w\s]{0,12}?white\s*-?\s*cast\b/.test(t)) raw.white_cast = false;
    var spf = /\bspf\s*(\d{2,3})\b/.exec(t);
    if (spf) raw.spf_min = parseInt(spf[1], 10);
    if (/\b(pregnan\w*|breast\s*-?feed\w*|nursing)\b/.test(t)) raw.pregnancy_safe = true;
    if (/\b(fragrance|perfume)[\s-]*free\b|\bno\s*(fragrance|perfume)\b/.test(t)) raw.fragrance_free = true;
    if (/\bmatte\b/.test(t)) raw.finish = 'matte';
    if (/\b(routine|regimen|regime)\b/.test(t)) raw.mode = 'routine';

    var intent = sanitizeIntent(raw);
    if (norm(text) && !intent.category && !intent.skin_types.length && !intent.concerns.length &&
        !intent.max_price && !intent.usage_time && intent.mode !== 'routine') {
      intent.unclear = ['Could not pick out a product type, skin type or concern. Try the checklist below.'];
    }
    return intent;
  }

  /** Merges two intents; values in `over` win (used for checklist edits on top of typed text). */
  function mergeIntents(base, over) {
    var a = sanitizeIntent(base), b = sanitizeIntent(over), out = sanitizeIntent(a);
    ['category', 'max_price', 'usage_time', 'white_cast', 'spf_min', 'pregnancy_safe', 'fragrance_free', 'finish'].forEach(function (k) {
      if (b[k] !== null) out[k] = b[k];
    });
    if (b.skin_types.length) out.skin_types = b.skin_types;
    if (b.concerns.length) out.concerns = b.concerns;
    if (b.mode === 'routine') out.mode = 'routine';
    return out;
  }

  // ---------------------------------------------------------------------------
  // Matching
  // ---------------------------------------------------------------------------

  function srcNote(p, key) {
    var s = p.source[key];
    if (s === 'collection') return ' (from its Shopify collection)';
    if (s === 'title') return ' (from the product name)';
    if (s === 'category') return ' (' + label(p.attrs.category).toLowerCase() + ')';
    return '';
  }

  /**
   * Scores one product. Returns null when a hard requirement fails
   * (wrong category, over budget, out of stock, confirmed white cast,
   * not confirmed pregnancy-safe when that was asked for, or listed as
   * avoid-with for a product the shopper named).
   */
  // A requirement the product has no data for yet earns half credit: it is
  // neither confirmed nor ruled out, and the card says so in its conflicts.
  var UNKNOWN = 0.5;

  function scoreProduct(p, intent) {
    var a = p.attrs, reasons = [], conflicts = [], matched = {};
    var earned = 0, possible = 0;

    if (intent.category) {
      if (a.category !== intent.category) return null;
      reasons.push('Is a ' + label(a.category).toLowerCase() + srcNote(p, 'category'));
      matched.category = a.category;
    }
    if (!p.available) return null;
    if (intent.max_price && rupees(p.price) > intent.max_price) return null;
    if (intent.max_price) {
      reasons.push('Within budget: ₹' + rupees(p.price).toLocaleString('en-IN') + ' (max ₹' + intent.max_price.toLocaleString('en-IN') + ')');
      matched.max_price = rupees(p.price);
    }
    if (intent.white_cast === false && a.white_cast === true) return null;
    if (intent.pregnancy_safe && a.pregnancy_safe !== true) return null;

    if (intent.skin_types.length) {
      possible += 3;
      var st = (a.skin_types || []).filter(function (s) { return intent.skin_types.indexOf(s) !== -1; });
      if (st.length) {
        earned += 3;
        reasons.push('Suitable for ' + st.map(label).join(', ').toLowerCase() + ' skin' + srcNote(p, 'skin_types'));
        matched.skin_types = st;
      } else if (!(a.skin_types || []).length) {
        earned += UNKNOWN * 3;
        conflicts.push('Skin-type suitability not listed yet');
      } else {
        conflicts.push('Listed for ' + a.skin_types.map(label).join(', ').toLowerCase() + ' skin');
      }
    }

    if (intent.concerns.length) {
      var per = 4 / intent.concerns.length;
      intent.concerns.forEach(function (c) {
        possible += per;
        if ((a.concerns || []).indexOf(c) !== -1) {
          earned += per;
          reasons.push('Targets ' + label(c).toLowerCase() + srcNote(p, 'concerns'));
          (matched.concerns = matched.concerns || []).push(c);
        } else if (!(a.concerns || []).length) {
          earned += UNKNOWN * per;
          conflicts.push('Concerns not listed yet');
        } else {
          conflicts.push('Not listed for ' + label(c).toLowerCase());
        }
      });
    }

    if (intent.usage_time && intent.usage_time !== 'both') {
      possible += 2;
      if (a.usage_time === intent.usage_time || a.usage_time === 'both') {
        earned += 2;
        reasons.push('For ' + (intent.usage_time === 'am' ? 'morning' : 'night') + ' use' + srcNote(p, 'usage_time'));
        matched.usage_time = a.usage_time;
      } else if (!a.usage_time) {
        earned += UNKNOWN * 2;
        conflicts.push('Morning/night use not listed yet');
      } else {
        conflicts.push('Meant for ' + label(a.usage_time).toLowerCase() + ' use');
      }
    }

    if (intent.white_cast === false) {
      possible += 2;
      if (a.white_cast === false) { earned += 2; reasons.push('No white cast'); matched.white_cast = false; }
      else { earned += UNKNOWN * 2; conflicts.push('White cast not confirmed'); }
    }

    if (intent.spf_min) {
      possible += 2;
      if (a.spf >= intent.spf_min) {
        earned += 2;
        reasons.push('SPF ' + a.spf + (a.pa ? ' ' + a.pa : '') + srcNote(p, 'spf'));
        matched.spf = a.spf;
      } else if (a.spf) {
        conflicts.push('SPF ' + a.spf + ' is below SPF ' + intent.spf_min);
      } else {
        earned += UNKNOWN * 2;
        conflicts.push('SPF not listed');
      }
    }

    if (intent.fragrance_free) {
      possible += 1;
      if (a.fragrance_free === true) { earned += 1; reasons.push('Fragrance-free'); }
      else if (a.fragrance_free === false) conflicts.push('Contains fragrance');
      else { earned += UNKNOWN; conflicts.push('Fragrance-free not confirmed'); }
    }

    if (intent.finish) {
      possible += 1;
      if (a.finish === intent.finish) { earned += 1; reasons.push(label(a.finish) + ' finish'); }
      else if (a.finish) conflicts.push(label(a.finish) + ' finish');
      else { earned += UNKNOWN; conflicts.push('Finish not listed'); }
    }

    if (intent.pregnancy_safe) reasons.push('Marked pregnancy-safe');

    var score = possible ? earned / possible : 1;
    return { product: p, score: Math.round(score * 100) / 100, reasons: reasons, conflicts: conflicts, matched: matched };
  }

  function matchProducts(intent, catalog, options) {
    var limit = (options && options.limit) || 12;
    var minScore = (options && options.minScore) || 0.34;
    intent = sanitizeIntent(intent);
    var results = [];
    catalog.forEach(function (p) {
      var r = scoreProduct(p, intent);
      if (r && r.score >= minScore) results.push(r);
    });
    results.sort(function (x, y) {
      return (y.score - x.score) || (y.reasons.length - x.reasons.length) || (x.product.price - y.product.price);
    });
    return results.slice(0, limit);
  }

  // ---------------------------------------------------------------------------
  // Routine builder
  // ---------------------------------------------------------------------------

  /**
   * One product per routine step for the requested time of day, chosen by
   * match score and kept within the budget (swapping the priciest step for a
   * cheaper match until it fits). Sunscreen only in the morning.
   */
  function buildRoutine(intent, catalog) {
    intent = sanitizeIntent(intent);
    var time = intent.usage_time === 'pm' ? 'pm' : 'am';
    var stepsWanted = time === 'am' ? [2, 3, 4, 5] : [2, 3, 4];
    var base = sanitizeIntent(intent);
    base.category = null;
    base.max_price = null;

    var options = {};
    stepsWanted.forEach(function (step) {
      var cands = catalog.filter(function (p) {
        if (p.attrs.routine_step !== step) return false;
        if (time === 'pm' && p.attrs.usage_time === 'am') return false;
        if (time === 'am' && p.attrs.usage_time === 'pm') return false;
        return true;
      }).map(function (p) { return scoreProduct(p, base); }).filter(Boolean);
      cands.sort(function (x, y) { return (y.score - x.score) || (x.product.price - y.product.price); });
      options[step] = cands;
    });

    var pick = {};
    stepsWanted.forEach(function (s) { if (options[s].length) pick[s] = 0; });

    function total() {
      return Object.keys(pick).reduce(function (sum, s) { return sum + rupees(options[s][pick[s]].product.price); }, 0);
    }

    if (intent.max_price) {
      var guard = 0;
      while (total() > intent.max_price && guard++ < 50) {
        // Swap the step whose next cheaper option saves the most.
        var best = null;
        Object.keys(pick).forEach(function (s) {
          var cur = options[s][pick[s]];
          for (var k = pick[s] + 1; k < options[s].length; k++) {
            var save = cur.product.price - options[s][k].product.price;
            if (save > 0 && (!best || save > best.save)) { best = { step: s, idx: k, save: save }; break; }
          }
        });
        if (!best) break;
        pick[best.step] = best.idx;
      }
    }

    var steps = stepsWanted.filter(function (s) { return pick[s] !== undefined; }).map(function (s) {
      var r = options[s][pick[s]];
      return { step: s, name: STEP_NAMES[s], result: r };
    });
    var missing = stepsWanted.filter(function (s) { return pick[s] === undefined; }).map(function (s) { return STEP_NAMES[s]; });
    var sum = total();
    return {
      time: time,
      steps: steps,
      missing_steps: missing,
      total: sum,
      budget: intent.max_price,
      within_budget: intent.max_price ? sum <= intent.max_price : true
    };
  }

  // ---------------------------------------------------------------------------
  // Comparison
  // ---------------------------------------------------------------------------

  function compare(a, b) {
    var keys = ['category', 'skin_types', 'concerns', 'usage_time', 'finish', 'texture', 'white_cast', 'spf', 'pa', 'pregnancy_safe', 'fragrance_free'];
    var rows = [{ key: 'price', a: rupees(a.price), b: rupees(b.price), same: a.price === b.price }];
    keys.forEach(function (k) {
      var va = a.attrs[k], vb = b.attrs[k];
      if (va === undefined && vb === undefined) return;
      rows.push({ key: k, a: va === undefined ? null : va, b: vb === undefined ? null : vb, same: JSON.stringify(va) === JSON.stringify(vb) });
    });
    return { a: a.title, b: b.title, rows: rows, differences: rows.filter(function (r) { return !r.same; }).map(function (r) { return r.key; }) };
  }

  return {
    VOCAB: VOCAB,
    label: label,
    rupees: rupees,
    normalizeProduct: normalizeProduct,
    normalizeCatalog: normalizeCatalog,
    emptyIntent: emptyIntent,
    sanitizeIntent: sanitizeIntent,
    parseIntentLocal: parseIntentLocal,
    mergeIntents: mergeIntents,
    scoreProduct: scoreProduct,
    matchProducts: matchProducts,
    buildRoutine: buildRoutine,
    compare: compare
  };
});
